import { setUserNavigationMethod } from 'src/app/@utils/accessibility-helper';
import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { LoggingService } from '@core/services/logging.service';
import { MetaService } from '@core/services/meta.service';
import { PlatformService } from '@core/services/platform.service';
import { SchemaService } from '@core/services/schema.service';
import { SettingsService } from '@core/services/settings.service';
import { SiteContextService } from '@core/services/site-context.service';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { UrlHelperService } from '@core/services/url-helper.service';
import { PageTypes } from '@features/page/page.config';
import { GlobalSeoViewModel, StructuredDataViewModel, TrackingSetupViewModel } from '@models/api.models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { switchMap, take } from 'rxjs/operators';
import { ClubService } from '@features/club/service/club.service';
import { ConsentType } from '@core/services/cookie-consent/cookie-consent.config';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { of } from 'rxjs';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { LayoutModule } from '@features/layout/layout.module';
import { CommonModule, ViewportScroller } from '@angular/common';

@Component({
    imports: [LayoutModule, CommonModule, RouterModule, TranslateModule],
    selector: 'app-root',
    standalone: true,
    template: `
        <div class="app">
            <button class="skip-link" (click)="mainContentFocus()">
                {{ 'general.accessibility.skipLinkDescription' | translate }}
            </button>
            <app-backdrop
                [visible]="(siteStateService.state$ | async)?.backdrop"
                (backdropClickEvent)="backdropClick()"
            ></app-backdrop>

            @if ((settingsService.settings$ | async)?.headerContent; as headerContent) {
                <app-notification-bar
                    *ngIf="headerContent"
                    [active]="headerContent.active"
                    [content]="headerContent.content"
                    [textColor]="headerContent.textColor"
                    [backgroundColor]="'#' + headerContent.backgroundColor"
                ></app-notification-bar>
            }
            <!-- Look at backdrop to make sure we don't hide the header if anything from header is open, e.g. menu, minibasket etc. -->
            <header
                [ngClass]="{
                    header_hidden:
                        (this.scrollService.hideHeader$ | async) === true &&
                        !((siteStateService.state$ | async)?.backdrop || (siteStateService.state$ | async)?.mobileMenu)
                }"
                class="header"
            >
                <app-header></app-header>
            </header>
            <main class="main" id="main-content" role="main" tabindex="0">
                <ng-container *ngIf="siteContextService.context$ | async as context">
                    <app-breadcrumb
                        class="container"
                        *ngIf="
                            context.type !== pageTypes.ErrorPage &&
                            context.type !== pageTypes.RootPage &&
                            context.type !== pageTypes.ProductDetailPage
                        "
                    ></app-breadcrumb>
                </ng-container>
                <router-outlet></router-outlet>
            </main>
            <footer class="footer">
                <app-footer></app-footer>
            </footer>
            <app-notifications></app-notifications>
        </div>
    `,
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, AfterViewInit {
    public pageTypes = PageTypes;

    constructor(
        private translateService: TranslateService,
        private urlService: UrlHelperService,
        private platformService: PlatformService,
        private gtmService: GtmService,
        private loggingService: LoggingService,
        private cookieConsentService: CookieConsentService,
        private metaService: MetaService,
        private authenticationService: AuthenticationService,
        private clubService: ClubService,
        private relewiseTrackingService: RelewiseTrackingService,
        private viewportScroller: ViewportScroller,
        private ngZone: NgZone,
        public settingsService: SettingsService,
        public siteStateService: SiteStateService,
        public router: Router,
        public scrollService: ScrollService,
        public siteContextService: SiteContextService,
        public schemaService: SchemaService
    ) {
        // Dentsu is really adamant about this being as early in the process as possible
        // Although it is most likely invoked before GTM that is reliant on settings, we place it here for good measure
        if (this.platformService.isBrowser) {
            this.cookieConsentService.initCookieConsent();
        }

        this.authenticationService.loginState$
            .pipe(switchMap(({ loggedIn }) => (loggedIn ? this.clubService.getUserInfo() : of({ customerNo: '' }))))
            .subscribe(userInfo => {
                this.gtmService.setUserData(userInfo);
            });

        this.cookieConsentService.consentStatus$.subscribe(_ => {
            this.relewiseTrackingService.trackAgilicUser();
        });

        this.loggingService.initSentry();
        this.loggingService.initAI();
    }

    ngOnInit(): void {
        this.translateService.use(this.urlService.getCurrentMarket);
        if (this.platformService.isBrowser) {
            setUserNavigationMethod();
            this.viewportScroller.setOffset([0, 200]);
        }
    }

    ngAfterViewInit(): void {
        this.settingsService.settings$
            .pipe(take(1))
            .subscribe(({ trackingSetup, globalSeo, structuredData, customScripts }) => {
                this.ngZone.runOutsideAngular(() => {
                    this.initSeo(trackingSetup, globalSeo, structuredData);

                    if (customScripts?.snippets?.length) {
                        this.initCustomSnippets(customScripts.snippets);
                    }
                });
            });
    }

    public backdropClick(): void {
        this.siteStateService.resetState();
    }

    public mainContentFocus(): void {
        if (this.platformService.isBrowser) {
            const mainContent = document.getElementById('main-content');
            mainContent?.focus();
        }
    }

    private initCustomSnippets(snippets: string[]): void {
        snippets.forEach((snippet, index) => {
            this.cookieConsentService.addSnippetToLoadIfConsentGiven({
                snippet,
                id: `snippet-${index}`,
                scriptOptions: {
                    position: 'beforeBodyEnd'
                },
                type: ConsentType.Marketing
            });
        });
    }

    private initSeo(
        trackingSetup?: TrackingSetupViewModel,
        globalSeo?: GlobalSeoViewModel,
        structuredData?: StructuredDataViewModel
    ): void {
        if (trackingSetup) {
            this.gtmService.initGTM(trackingSetup);
        }

        if (globalSeo) {
            const { htmlLang, authorName, copyright } = globalSeo;

            this.metaService.setHtmlLang(htmlLang);
            this.metaService.setAuthor(authorName);
            this.metaService.setCopyright(copyright);
        }

        if (structuredData) {
            const { wType: websiteSchema, oType: organizationSchema } = structuredData;

            this.schemaService.setUmbracoSchema(websiteSchema, organizationSchema);
        }
    }
}
